import { useEffect, useMemo, useState } from 'react';
import {
  listSentencesByChapter,
  createSentence,
  updateSentence, // eslint-disable-line no-unused-vars
  deleteSentence,
  editSentence,
} from '@dataconnect/generated';
import UploadAssetButton from '../../utilities/ui/UploadAssetButton';
import ImageLoader from '../../utilities/ui/ImageLoader';
import AudioLoader from '../../utilities/ui/AudioLoader';
import { getStorage, ref, uploadBytes, deleteObject } from "firebase/storage";
import { useAuthState } from 'react-firebase-hooks/auth';
import { getAuth } from 'firebase/auth';
import { db } from '../../firebase';
import { extractTextFromImage, generateSentencesFromText } from '../../utilities/extensions/imageToText';
import { translateBatchToText } from '../../utilities/extensions/translateToText';
import Constants from '../../utilities/constants/Constants';
import { generateBatchSpeechAudio, LanguageCodes, VoiceGenders } from '../../utilities/extensions/textToSpeech';
import { validateEnglishSentence } from '../../utilities/validation/textValidation';
import { generateSentenceStoragePath } from '../../utilities/storagePathHelper';

const AdminSentences = ({ selectedChapter }) => { // eslint-disable-line no-unused-vars
  const [sentences, setSentences] = useState([]);
  const [adding, setAdding] = useState(false);
  const [creating, setCreating] = useState(false);
  const [loading, setLoading] = useState(true); // eslint-disable-line no-unused-vars
  const [err, setErr] = useState(null);
  const [form, setForm] = useState({
    languageText: '',
    englishText: '',
    imageUrl: '',
    languageAudioUrl: '',
    englishAudioUrl: '',
  });

  // Image upload for sentence generation
  const [showImageUpload, setShowImageUpload] = useState(false);
  const [imageProcessing, setImageProcessing] = useState(false);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [firestoreDocId, setFirestoreDocId] = useState(null);
  const [generatedSentences, setGeneratedSentences] = useState([]);
  const [extractedText, setExtractedText] = useState(''); // eslint-disable-line no-unused-vars
  const [translating, setTranslating] = useState(false);
  const [imageBlobUrl, setImageBlobUrl] = useState(null);
  const [generatingAudio, setGeneratingAudio] = useState(false);
  const [hasTranslations, setHasTranslations] = useState(false);

  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({
    languageText: '',
    englishText: '',
    imageUrl: '',
    languageAudioUrl: '',
    englishAudioUrl: '',
  });
  const [savingId, setSavingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const storage = getStorage();
  const auth = getAuth();
  const [user] = useAuthState(auth); // eslint-disable-line no-unused-vars

  const nextDisplayOrder = useMemo(
    () => (sentences.length ? Math.max(...sentences.map(s => s.displayOrder || 0)) + 1 : 1),
    [sentences]
  );

  async function refresh() {
    setErr(null);
    setLoading(true);
    try {
      const res = await listSentencesByChapter({ "chapterId": selectedChapter?.id });
      const items =
        Array.isArray(res) ? res :
          Array.isArray(res?.items) ? res.items :
            Array.isArray(res?.data?.sentences) ? res.data.sentences :
              [];
      setSentences(items);
    } catch (e) {
      setErr(e.message || 'Failed to load sentences');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refresh();
  }, []);

  // Cleanup blob URL on component unmount
  useEffect(() => {
    return () => {
      if (imageBlobUrl) {
        URL.revokeObjectURL(imageBlobUrl);
      }
    };
  }, [imageBlobUrl]);

  // ---- Create ----
  // ----- handleAdd -----
  async function handleAdd(e) {
    e.preventDefault();

    if (!selectedChapter?.id) {
      setErr("Please select a chapter first.");
      return;
    }

    // Validate English text
    const englishError = validateEnglishSentence(form.englishText, 'English text');
    if (englishError) {
      setErr(englishError);
      return;
    }

    setErr(null);
    setCreating(true);

    // base fields for the initial create
    const base = {
      chapterId: selectedChapter.id,
      displayOrder: Number(nextDisplayOrder) || 0,
      languageText: (form.languageText || "").trim(),
      englishText: (form.englishText || "").trim(),
    };

    try {
      // 1) create db row to get a sentenceId
      const { data } = await createSentence(base);
      const sentenceId = data?.sentence_insert?.id;
      if (!sentenceId) throw new Error("Sentence ID not returned from create.");

      // 2) upload any files in parallel
      const uploadTasks = [];

      if (form.imageFile) {
        uploadTasks.push(
          uploadFile({ kind: "image", sentenceId, file: form.imageFile })
        );
      }
      if (form.languageAudioFile) {
        uploadTasks.push(
          uploadFile({
            kind: "languageAudioFile",
            sentenceId,
            file: form.languageAudioFile,
          })
        );
      }
      if (form.englishAudioFile) {
        uploadTasks.push(
          uploadFile({
            kind: "englishAudioFile",
            sentenceId,
            file: form.englishAudioFile,
          })
        );
      }
      const results = await Promise.all(uploadTasks);

      // 3) map upload results → db fields and update only if needed
      const updates = {};
      for (const r of results) {
        if (r.kind === "image")
          updates.imageUrl = r.path;
        if (r.kind === "languageAudioFile")
          updates.languageAudioUrl = r.path;
        if (r.kind === "englishAudioFile")
          updates.englishAudioUrl = r.path;
      }

      if (Object.keys(updates).length > 0) {
        await editSentence({ id: sentenceId, ...updates });
      }

      await refresh();
      setForm({
        displayOrder: "",
        languageText: "",
        englishText: "",
        imageUrl: "",
        languageAudioUrl: "",
        englishAudioUrl: "",
        imageFile: "",
        languageAudioFile: "",
        englishAudioFile: "",
      });
      setAdding(false);
    } catch (err) {

      setErr(err?.message || "Failed to create sentence");
    } finally {
      setCreating(false);
    }
  }

  // ----- uploadFile -----
  // Returns: { kind, path, downloadURL, name }
  async function uploadFile({ kind, sentenceId, file }) {
    try {
      const filePath = buildStoragePath({
        env: "dev",
        language: form.language || "language",
        grade: selectedChapter?.grade,
        chapterId: selectedChapter?.id,
        sentenceId,
        kind,
        fileName: file.name,
      });

      const storageRef = ref(storage, filePath);
      const snap = await uploadBytes(storageRef, file);

      return {
        kind,
        path: snap.metadata.fullPath,
        name: snap.metadata.name,
      };
    } catch (err) {

      throw new Error(err?.message || `Failed to upload ${kind}`);
    }
  }

  // ---- Edit ----
  function startEdit(s) {
    setEditId(s.id);
    setEditForm({
      languageText: s.languageText ?? '',
      englishText: s.englishText ?? '',
      imageUrl: s.imageUrl ?? '',
      languageAudioUrl: s.languageAudioUrl ?? '',
      englishAudioUrl: s.englishAudioUrl ?? '',
      imageFile: '',
      languageAudioFile: '',
      englishAudioFile: ''
    });
  }

  async function saveEdit(id) {
    // Validate English text
    const englishError = validateEnglishSentence(editForm.englishText, 'English text');
    if (englishError) {
      setErr(englishError);
      return;
    }

    setSavingId(id);
    setErr(null);
    try {
      if (editForm.imageFile) {
        const res = await uploadFile({ kind: "image", sentenceId: id, file: editForm.imageFile });
        if (editForm.imageUrl) {
          await deleteFileFromStorage(editForm.imageUrl);
        }
        editForm.imageUrl = res.path;
      }
      if (editForm.languageAudioFile) {
        const res = await uploadFile({ kind: "languageAudioFile", id, file: editForm.languageAudioFile })
        if (editForm.languageAudioUrl) {
          await deleteFileFromStorage(editForm.languageAudioUrl);
        }
        editForm.languageAudioUrl = res.path;
      }
      if (editForm.englishAudioFile) {
        const res = await uploadFile({ kind: "englishAudioFile", id, file: editForm.englishAudioFile })
        if (editForm.englishAudioUrl) {
          await deleteFileFromStorage(editForm.englishAudioUrl);
        }
        editForm.englishAudioUrl = res.path;
      }
      await editSentence({
        id,
        languageText: editForm.languageText,
        englishText: editForm.englishText,
        imageUrl: editForm.imageUrl,
        languageAudioUrl: editForm.languageAudioUrl,
        englishAudioUrl: editForm.englishAudioUrl
      });
      await refresh();
      setEditId(null);
    } catch (e) {
      setErr(e.message || 'Failed to update sentence');
    } finally {
      setSavingId(null);
    }
  }

  function cancelEdit() {
    setEditId(null);
  }

  // ---- Delete ----
  async function remove(s) {
    if (!window.confirm('Delete this sentence?')) return;
    setDeletingId(s.id);
    setErr(null);
    try {
      if (s.imageUrl) {
        await deleteFileFromStorage(s.imageUrl);
      }
      if (s.languageAudioUrl) {
        await deleteFileFromStorage(s.languageAudioUrl);
      }
      if (s.englishAudioUrl) {
        await deleteFileFromStorage(s.englishAudioUrl);
      }
      await deleteSentence({ id: s.id });
      await refresh();
    } catch (e) {
      setErr(e.message || 'Failed to delete sentence');
    } finally {
      setDeletingId(null);
    }
  }

  async function deleteFileFromStorage(url) {
    const deleteRef = ref(storage, url);
    await deleteObject(deleteRef);
  }

  // Image upload and processing for sentence generation
  async function handleImageUpload(event) {
    const file = event.target.files[0];
    if (!file) {
      return;
    }

    setUploadedImage(file);
    
    // Create blob URL once and store it
    const blobUrl = URL.createObjectURL(file);
    setImageBlobUrl(blobUrl);
    
    setImageProcessing(true);
    setErr(null);

    try {
      // Process the image - this handles upload internally
      const sentences = await processImageForSentences(file);
      setGeneratedSentences(sentences);
    } catch (error) {

      setErr('Failed to process image: ' + error.message);
      setGeneratedSentences([]);
    } finally {
      setImageProcessing(false);
    }
  }


  // Process uploaded image and generate sentences using AI
  async function processImageForSentences(imageFile) {
    if (!imageFile || !selectedChapter?.id) {
      throw new Error('No image file or chapter not selected');
    }

    try {
      // Step 1: Extract text from image using Google Vision API (handles upload internally)
      const extractedText = await extractTextFromImage(imageFile, storage, db);

      // Store the extracted text for translation option
      setExtractedText(extractedText);

      // Step 2: Generate sentences from the extracted text
      const sentences = generateSentencesFromText(extractedText);

      if (sentences.length === 0) {
        throw new Error('No sentences could be generated from the extracted text. The image may not contain readable text.');
      }

      return sentences;
    } catch (error) {
      throw new Error('Failed to process image: ' + error.message);
    }
  }



  // Create multiple sentences from generated list
  async function createBulkSentences() {
    if (!selectedChapter?.id || generatedSentences.length === 0) {
      setErr("No sentences to create or chapter not selected.");
      return;
    }

    setCreating(true);
    setErr(null);

    try {
      let currentDisplayOrder = nextDisplayOrder;
      
      for (let i = 0; i < generatedSentences.length; i++) {
        const sentence = generatedSentences[i];
        
        const base = {
          chapterId: selectedChapter.id,
          displayOrder: currentDisplayOrder,
          languageText: sentence.languageText.trim(),
          englishText: sentence.englishText.trim(),
        };

        // Include audio URLs if they exist
        if (sentence.languageAudioUrl) {
          base.languageAudioUrl = sentence.languageAudioUrl;
        }
        if (sentence.englishAudioUrl) {
          base.englishAudioUrl = sentence.englishAudioUrl;
        }

        await createSentence(base);
        currentDisplayOrder++;
      }

      await refresh();
      
      setGeneratedSentences([]);
      if (imageBlobUrl) {
        URL.revokeObjectURL(imageBlobUrl);
      }
      setUploadedImage(null);
      setImageBlobUrl(null);
      setFirestoreDocId(null);
      setShowImageUpload(false);
      setExtractedText('');
      setHasTranslations(false);
    } catch (error) {

      setErr('Failed to create sentences: ' + error.message);
    } finally {
      setCreating(false);
    }
  }

  // Edit a generated sentence before creating
  function editGeneratedSentence(index, field, value) {
    const updated = [...generatedSentences];
    updated[index][field] = value;
    setGeneratedSentences(updated);
  }

  // Remove a generated sentence
  function removeGeneratedSentence(index) {
    const updated = generatedSentences.filter((_, i) => i !== index);
    setGeneratedSentences(updated);
  }

  // Add a new empty row above the specified index
  function addRowAbove(index) {
    const newSentence = {
      languageText: '',
      englishText: ''
    };
    const updated = [...generatedSentences];
    updated.splice(index, 0, newSentence);
    setGeneratedSentences(updated);
  }

  // Translate extracted text to English
  async function translateExtractedText() {
    if (generatedSentences.length === 0) {
      setErr('No sentences available for translation');
      return;
    }

    setTranslating(true);
    setErr(null);

    try {
      // Extract all language texts from generated sentences
      const languageTexts = generatedSentences.map(sentence => sentence.languageText);
      
      // Use Firestore-based batch translation (Kannada to English)
      const translatedTexts = await translateBatchToText(
        languageTexts, 
        db, 
        5, 
        2000, 
        Constants.LANGUAGES.KANNADA, 
        Constants.LANGUAGES.ENGLISH
      );
      
      // Update existing sentences with English translations
      const updatedSentences = generatedSentences.map((sentence, index) => ({
        ...sentence,
        englishText: translatedTexts[index] || sentence.englishText
      }));
      
      setGeneratedSentences(updatedSentences);
      setHasTranslations(true);
    } catch (error) {

      setErr('Failed to translate text: ' + error.message);
    } finally {
      setTranslating(false);
    }
  }

  // Generate audio for all sentences
  async function generateAudioForSentences() {
    if (generatedSentences.length === 0) {
      setErr('No sentences available for audio generation');
      return;
    }

    setGeneratingAudio(true);
    setErr(null);

    try {
      // Prepare TTS requests for both language and English texts
      const ttsRequests = [];
      
      generatedSentences.forEach((sentence, index) => {
        // Add language audio request
        if (sentence.languageText && sentence.languageText.trim()) {
          ttsRequests.push({
            text: sentence.languageText,
            languageCode: LanguageCodes.KANNADA, // Assuming Language for now
            ssmlGender: VoiceGenders.FEMALE,
            sentenceIndex: index,
            type: 'language'
          });
        }
        
        // Add English audio request if translation exists
        if (sentence.englishText && sentence.englishText.trim()) {
          ttsRequests.push({
            text: sentence.englishText,
            languageCode: LanguageCodes.ENGLISH,
            ssmlGender: VoiceGenders.FEMALE,
            sentenceIndex: index,
            type: 'english'
          });
        }
      });

      // Generate audio for all requests
      const audioUrls = await generateBatchSpeechAudio(
        ttsRequests.map(req => ({
          text: req.text,
          languageCode: req.languageCode,
          ssmlGender: req.ssmlGender
        })),
        db,
        storage
      );
      
      // Map audio URLs back to sentences
      const updatedSentences = [...generatedSentences];
      
      ttsRequests.forEach((request, requestIndex) => {
        const audioUrl = audioUrls[requestIndex];
        if (audioUrl && !audioUrl.startsWith('[TTS Error:')) {
          const { sentenceIndex, type } = request;
          if (type === 'language') {
            updatedSentences[sentenceIndex].languageAudioUrl = audioUrl;
          } else if (type === 'english') {
            updatedSentences[sentenceIndex].englishAudioUrl = audioUrl;
          }
        }
      });
      
      setGeneratedSentences(updatedSentences);
    } catch (error) {

      setErr('Failed to generate audio: ' + error.message);
    } finally {
      setGeneratingAudio(false);
    }
  }

  // Generate audio for existing sentences that are missing language/English audio
  async function generateAudioForExistingSentences() {
    if (!selectedChapter?.id || !sentences.length) {
      setErr('No sentences available for audio generation');
      return;
    }

    // Build TTS requests only for missing audio fields
    const ttsRequests = [];

    sentences.forEach((sentence) => {
      // Language audio missing
      if (
        sentence.languageText &&
        sentence.languageText.trim() &&
        !sentence.languageAudioUrl
      ) {
        ttsRequests.push({
          text: sentence.languageText,
          languageCode: LanguageCodes.KANNADA,
          ssmlGender: VoiceGenders.FEMALE,
          sentenceId: sentence.id,
          type: 'language',
        });
      }

      // English audio missing
      if (
        sentence.englishText &&
        sentence.englishText.trim() &&
        !sentence.englishAudioUrl
      ) {
        ttsRequests.push({
          text: sentence.englishText,
          languageCode: LanguageCodes.ENGLISH,
          ssmlGender: VoiceGenders.FEMALE,
          sentenceId: sentence.id,
          type: 'english',
        });
      }
    });

    if (ttsRequests.length === 0) {
      setErr('All sentences already have audio for the available texts');
      return;
    }

    setGeneratingAudio(true);
    setErr(null);

    try {
      const audioUrls = await generateBatchSpeechAudio(
        ttsRequests.map((req) => ({
          text: req.text,
          languageCode: req.languageCode,
          ssmlGender: req.ssmlGender,
        })),
        db,
        storage
      );

      // Collect updates per sentenceId
      const updatesBySentenceId = {};

      ttsRequests.forEach((request, index) => {
        const audioUrl = audioUrls[index];
        if (!audioUrl || audioUrl.startsWith('[TTS Error:')) return;

        const { sentenceId, type } = request;
        if (!updatesBySentenceId[sentenceId]) {
          updatesBySentenceId[sentenceId] = {};
        }

        if (type === 'language') {
          updatesBySentenceId[sentenceId].languageAudioUrl = audioUrl;
        } else if (type === 'english') {
          updatesBySentenceId[sentenceId].englishAudioUrl = audioUrl;
        }
      });

      // Persist updates
      const updatePromises = Object.entries(updatesBySentenceId).map(
        ([id, updates]) => editSentence({ id, ...updates })
      );

      if (updatePromises.length) {
        await Promise.all(updatePromises);
      }

      await refresh();
    } catch (error) {

      setErr('Failed to generate audio: ' + error.message);
    } finally {
      setGeneratingAudio(false);
    }
  }

  // CSV Export function
  const exportToCSV = () => {
    if (!sentences || sentences.length === 0) {
      alert('No sentences to export');
      return;
    }

    // Define CSV headers
    const headers = [
      'Display Order',
      'Language Text',
      'English Text',
      'Image URL',
      'Language Audio URL',
      'English Audio URL'
    ];

    // Convert sentences to CSV rows
    const csvRows = [
      headers.join(','), // Header row
      ...sentences.map(sentence => [
        sentence.displayOrder || '',
        `"${(sentence.languageText || '').replace(/"/g, '""')}"`, // Escape quotes
        `"${(sentence.englishText || '').replace(/"/g, '""')}"`,
        `"${(sentence.imageUrl || '').replace(/"/g, '""')}"`,
        `"${(sentence.languageAudioUrl || '').replace(/"/g, '""')}"`,
        `"${(sentence.englishAudioUrl || '').replace(/"/g, '""')}"`,
      ].join(','))
    ];

    // Create CSV content
    const csvContent = csvRows.join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${selectedChapter?.title || 'sentences'}_sentences.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Build the full payload your API expects (no extra fields)
  const fullPayload = (s, idx /* 0-based */) => ({
    id: s.id,
    chapterId: s.chapterId,
    displayOrder: idx + 1,        // API is 1-based
    languageText: s?.languageText ?? '',
    englishText: s?.englishText ?? '',
    imageUrl: s?.imageUrl ?? '',
    languageAudioUrl: s?.languageAudioUrl ?? '',
    englishAudioUrl: s?.englishAudioUrl ?? '',
  });

  async function moveUp(index) {
    if (index <= 0) return;

    const above = sentences[index - 1]; // goes down
    const cur = sentences[index];     // goes up

    setErr(null);
    setSavingId(cur.id);
    try {
      await Promise.all([
        editSentence(fullPayload(above, index)),     // above -> index+1
        editSentence(fullPayload(cur, index - 1)), // cur   -> index
      ]);
      await refresh();
      setEditId(null);
    } catch (e) {
      setErr(e.message || 'Failed to update sentence');
    } finally {
      setSavingId(null);
    }
  }

  function buildStoragePath({
    env = 'prod', // eslint-disable-line no-unused-vars
    language = 'language',
    grade,
    chapterId,
    sentenceId,
    kind,                  // 'image' | 'audio'
    variant = 'original',
    lang, // eslint-disable-line no-unused-vars
    fileName,
  }) {
    // Use the new storage path helper
    return generateSentenceStoragePath({
      language,
      grade: `grade${grade}`,
      chapterId,
      sentenceId,
      fileType: kind,
      variant,
      fileName
    });
  }

  async function moveDown(index) {
    if (index >= sentences.length - 1) return;

    const cur = sentences[index];       // goes down
    const below = sentences[index + 1];   // comes up

    setErr(null);
    setSavingId(cur.id);
    try {
      await Promise.all([
        editSentence(fullPayload(cur, index + 1)), // cur   -> index+2
        editSentence(fullPayload(below, index)),     // below -> index+1
      ]);
      await refresh();
      setEditId(null);
    } catch (e) {
      setErr(e.message || 'Failed to update sentence');
    } finally {
      setSavingId(null);
    }
  }

  return (
    <>
      <div className="container-fluid py-4 overflow-hidden">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 m-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Chapter: {selectedChapter.title}</h1>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              className="bg-emerald-500 hover:bg-emerald-600 text-white font-medium py-2 px-3 rounded-lg flex items-center gap-2 transition-colors text-sm"
              type="button"
              onClick={() => {
                if (showImageUpload) {
                  // Reset state when closing
                  if (imageBlobUrl) {
                    URL.revokeObjectURL(imageBlobUrl);
                  }
                  setUploadedImage(null);
                  setImageBlobUrl(null);
                  setFirestoreDocId(null);
                  setGeneratedSentences([]);
                  setExtractedText('');
                  setHasTranslations(false);
                }
                setShowImageUpload(v => !v);
              }}
            >
              <span>📷</span>
              {showImageUpload ? 'Close' : 'Generate from Image'}
            </button>
            <button
              className="bg-blue-500 hover:bg-blue-600 text-white font-medium py-2 px-3 rounded-lg transition-colors text-sm"
              type="button"
              onClick={() => setAdding(v => !v)}
            >
              {adding ? 'Close' : 'Add Sentence'}
            </button>
            <button
              className="bg-purple-500 hover:bg-purple-600 text-white font-medium py-2 px-3 rounded-lg transition-colors text-sm flex items-center gap-2"
              type="button"
              onClick={exportToCSV}
              disabled={!sentences || sentences.length === 0}
            >
              <span>📊</span>
              Export CSV
            </button>
            <button
              className="bg-orange-500 hover:bg-orange-600 text-white font-medium py-2 px-3 rounded-lg transition-colors text-sm flex items-center gap-2 disabled:bg-orange-400 disabled:cursor-not-allowed"
              type="button"
              onClick={generateAudioForExistingSentences}
              disabled={generatingAudio || !sentences || sentences.length === 0}
            >
              {generatingAudio ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  Generating Audio...
                </>
              ) : (
                <>
                  <span>🔊</span>
                  Generate Missing Audio
                </>
              )}
            </button>
          </div>
        </div>

        {/* Image Upload for Sentence Generation */}
        {showImageUpload && (
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm mb-6 overflow-hidden">
            <div className="bg-gradient-to-r from-emerald-50 to-teal-50 px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <span className="bg-emerald-100 text-emerald-600 p-2 rounded-lg">
                  <span className="text-lg">📷</span>
                </span>
                Generate Sentences from Image
              </h3>
              <p className="text-sm text-gray-600 mt-1">Upload an image to automatically extract and generate sentences using AI</p>
            </div>
            <div className="p-6">
            
            {!uploadedImage ? (
              <div className="text-center">
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 hover:border-emerald-400 transition-colors">
                  <div className="flex flex-col items-center">
                    <span className="text-4xl mb-4">🖼️</span>
                    <label htmlFor="imageUpload" className="text-lg font-medium text-gray-900 mb-2 cursor-pointer">
                      Upload an image to generate sentences
                    </label>
                    <input
                      type="file"
                      id="imageUpload"
                      className="hidden"
                      accept="image/*"
                      onChange={handleImageUpload}
                      disabled={imageProcessing}
                    />
                    <button
                      type="button"
                      className="bg-emerald-500 hover:bg-emerald-600 text-white font-medium py-3 px-6 rounded-lg transition-colors disabled:bg-emerald-400 disabled:cursor-not-allowed mb-3"
                      onClick={() => document.getElementById('imageUpload').click()}
                      disabled={imageProcessing}
                    >
                      Choose Image File
                    </button>
                    <p className="text-sm text-gray-500">
                      Supported formats: JPG, PNG, GIF. The AI will analyze the image and generate relevant sentences.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Uploaded Image Section */}
                <div className="lg:col-span-1">
                  <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <h4 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <span className="bg-blue-100 text-blue-600 p-2 rounded-lg">
                        <span className="text-lg">🖼️</span>
                      </span>
                      Uploaded Image
                    </h4>
                    <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                      <img 
                        src={imageBlobUrl} 
                        alt="Uploaded" 
                        className="w-full h-auto rounded-lg shadow-sm"
                        style={{ maxHeight: '300px', objectFit: 'contain' }}
                      />
                    </div>
                    <button 
                      className="w-full mt-4 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2 px-4 rounded-lg border border-gray-300 transition-colors flex items-center justify-center gap-2 text-sm"
                      onClick={() => {
                        // Reset state for new upload
                        if (imageBlobUrl) {
                          URL.revokeObjectURL(imageBlobUrl);
                        }
                        setUploadedImage(null);
                        setImageBlobUrl(null);
                        setFirestoreDocId(null);
                        setGeneratedSentences([]);
                        setExtractedText('');
                        setHasTranslations(false);
                      }}
                    >
                      <span>🔄</span>
                      Upload Different Image
                    </button>
                  </div>
                </div>
                
                {/* Generated Sentences Section */}
                <div className="lg:col-span-2">
                  {imageProcessing ? (
                    <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
                      <div className="flex flex-col items-center gap-4">
                        <div className="animate-spin rounded-full h-12 w-12 border-4 border-emerald-200 border-t-emerald-500"></div>
                        <div>
                          <h4 className="text-lg font-semibold text-gray-900 mb-1">Processing Image</h4>
                          <p className="text-gray-600 text-sm">Analyzing image and generating sentences using AI...</p>
                        </div>
                      </div>
                    </div>
                  ) : generatedSentences.length > 0 ? (
                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200">
                      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
                        <div>
                          <p className="text-sm font-medium text-gray-700 flex items-center gap-2">
                            <span className="text-sm">✨</span>
                            Generated sentences ({generatedSentences.length})
                          </p>
                          {firestoreDocId && (
                            <p className="text-xs text-gray-500 mt-1">
                              Document ID: <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">{firestoreDocId}</code>
                            </p>
                          )}
                        </div>
                        <div className="flex gap-2">
                          {generatedSentences.length > 0 && (
                            <button 
                              className="bg-purple-500 hover:bg-purple-600 text-white font-medium py-2 px-4 rounded-lg transition-colors disabled:bg-purple-400 disabled:cursor-not-allowed flex items-center gap-2 text-sm"
                              onClick={translateExtractedText}
                              disabled={translating || creating}
                            >
                              {translating ? (
                                <>
                                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                                  Translating...
                                </>
                              ) : (
                                <>
                                  <span>🌐</span>
                                  Translate to English
                                </>
                              )}
                            </button>
                          )}
                          {hasTranslations && generatedSentences.length > 0 && (
                            <button 
                              className="bg-orange-500 hover:bg-orange-600 text-white font-medium py-2 px-4 rounded-lg transition-colors disabled:bg-orange-400 disabled:cursor-not-allowed flex items-center gap-2 text-sm"
                              onClick={generateAudioForSentences}
                              disabled={generatingAudio || creating}
                            >
                              {generatingAudio ? (
                                <>
                                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                                  Generating Audio...
                                </>
                              ) : (
                                <>
                                  <span>🔊</span>
                                  Generate Audio
                                </>
                              )}
                            </button>
                          )}
                          <button 
                            className="bg-green-500 hover:bg-green-600 text-white font-medium py-2 px-4 rounded-lg transition-colors disabled:bg-green-400 disabled:cursor-not-allowed flex items-center gap-2 text-sm"
                            onClick={createBulkSentences}
                            disabled={creating}
                          >
                            {creating ? (
                              <>
                                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                                Creating...
                              </>
                            ) : (
                              <>
                                <span>💾</span>
                                Create All Sentences
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                      
                      <div className="space-y-4 max-h-96 overflow-y-auto">
                        {generatedSentences.map((sentence, index) => (
                          <div key={index} className="bg-white border border-gray-200 rounded-lg p-4">
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-3">
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Language Text</label>
                                <input
                                  type="text"
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                                  value={sentence.languageText}
                                  onChange={(e) => editGeneratedSentence(index, 'languageText', e.target.value)}
                                  placeholder="Enter text in the target language..."
                                />
                                {sentence.languageAudioUrl && (
                                  <div className="mt-2">
                                    <AudioLoader storagePath={sentence.languageAudioUrl} />
                                  </div>
                                )}
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">English Translation</label>
                                <input
                                  type="text"
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                                  value={sentence.englishText}
                                  onChange={(e) => editGeneratedSentence(index, 'englishText', e.target.value)}
                                  placeholder="Enter English translation..."
                                />
                                {sentence.englishAudioUrl && (
                                  <div className="mt-2">
                                    <AudioLoader storagePath={sentence.englishAudioUrl} />
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="flex justify-end gap-1">
                              <button 
                                className="text-blue-500 hover:text-blue-700 hover:bg-blue-50 p-2 rounded-lg transition-colors"
                                onClick={() => addRowAbove(index)}
                                title="Add row above this sentence"
                              >
                                <span className="text-lg">➕</span>
                              </button>
                              <button 
                                className="text-red-500 hover:text-red-700 hover:bg-red-50 p-2 rounded-lg transition-colors"
                                onClick={() => removeGeneratedSentence(index)}
                                title="Remove this sentence"
                              >
                                <span className="text-lg">🗑️</span>
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : uploadedImage ? (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                      <div className="flex flex-col items-center gap-4">
                        <span className="text-4xl">📝</span>
                        <div className="text-center">
                          <h4 className="text-lg font-semibold text-blue-900 mb-2">No sentences generated</h4>
                          <p className="text-blue-700 text-sm">The image processing didn&apos;t generate sentences. You can use the regular &quot;Add Sentence&quot; form above to create sentences manually.</p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
                      <div className="flex flex-col items-center gap-3">
                        <span className="text-4xl">🚀</span>
                        <div>
                          <h4 className="text-lg font-semibold text-blue-900 mb-1">Ready to process!</h4>
                          <p className="text-blue-700 text-sm">Click the upload button above to analyze the image and generate sentences.</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
            </div>
          </div>
        )}


        {/* Error Display */}
        {err && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mx-3 mb-4" role="alert">
            {err}
          </div>
        )}

        {/* Add Sentence */}
        {adding && (
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm mb-6 overflow-hidden">
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <span className="bg-blue-100 text-blue-600 p-2 rounded-lg">
                  <span className="text-lg">📝</span>
                </span>
                Add New Sentence
              </h3>
              <p className="text-sm text-gray-600 mt-1">Create a new sentence with text, image, and audio content</p>
            </div>
            <form className="p-6" onSubmit={handleAdd}>
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-end">
                {/* Language Text */}
                <div className="lg:col-span-3">
                  <label htmlFor="langText" className="block text-sm font-medium text-gray-700 mb-1">
                    Language Text <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="langText"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed transition-colors resize-none"
                    rows={2}
                    value={form.languageText}
                    onChange={(e) => setForm(f => ({ ...f, languageText: e.target.value }))}
                    placeholder="Local language text..."
                    disabled={creating}
                  />
                </div>

                {/* English Text */}
                <div className="lg:col-span-3">
                  <label htmlFor="engText" className="block text-sm font-medium text-gray-700 mb-1">
                    English Text
                  </label>
                  <textarea
                    id="engText"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed transition-colors resize-none"
                    rows={2}
                    value={form.englishText}
                    onChange={(e) => setForm(f => ({ ...f, englishText: e.target.value }))}
                    placeholder="English translation..."
                    disabled={creating}
                  />
                </div>

                {/* Upload Assets */}
                <div className="lg:col-span-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Media Assets
                  </label>
                  <div className="flex gap-2">
                    <UploadAssetButton
                      kind="image" 
                      label="Image"
                      className="flex-1 bg-gray-50 hover:bg-gray-100 border border-gray-300 text-gray-700 px-3 py-2 rounded-md text-sm font-medium transition-colors"
                      onFileUploaded={(file) => form.imageFile = file}
                    />
                    <UploadAssetButton
                      kind="audio" 
                      label="Lang Audio"
                      className="flex-1 bg-gray-50 hover:bg-gray-100 border border-gray-300 text-gray-700 px-3 py-2 rounded-md text-sm font-medium transition-colors"
                      onFileUploaded={(file) => form.languageAudioFile = file}
                    />
                    <UploadAssetButton
                      kind="audio" 
                      label="Eng Audio"
                      className="flex-1 bg-gray-50 hover:bg-gray-100 border border-gray-300 text-gray-700 px-3 py-2 rounded-md text-sm font-medium transition-colors"
                      onFileUploaded={(file) => form.englishAudioFile = file}
                    />
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="lg:col-span-2 flex gap-2">
                  <button 
                    className="bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white font-medium py-2 px-4 rounded-md transition-colors disabled:cursor-not-allowed flex items-center gap-2" 
                    type="submit" 
                    disabled={creating}
                  >
                    {creating ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Saving...
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Save
                      </>
                    )}
                  </button>
                  <button
                    className="border border-gray-300 hover:bg-gray-50 text-gray-700 font-medium py-2 px-4 rounded-md transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed"
                    type="button"
                    onClick={() => setForm({ languageText: '', englishText: '', imageUrl: '', languageAudioUrl: '', englishAudioUrl: '' })}
                    disabled={creating}
                  >
                    Clear
                  </button>
                </div>
              </div>
            </form>
          </div>
        )}

        {/* Sentences table */}
        <div className="flex items-center justify-between mb-4">
          <div className="text-gray-600 font-medium">{sentences.length} sentence{sentences.length !== 1 ? 's' : ''}</div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full bg-white border border-gray-200 rounded-lg overflow-hidden">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">Order</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Language Text</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">English Text</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Image</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Lang Audio</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Eng Audio</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-56">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sentences.map((s, index) => {
                const isEditing = editId === s.id;
                return (
                  <tr key={s.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="flex items-center gap-2">
                        <span className="bg-gray-100 text-gray-800 px-2 py-1 rounded-full text-xs font-medium">{s.displayOrder}</span>
                        <div className="flex" role="group">
                          <button
                            className="border border-gray-300 hover:bg-gray-50 text-gray-600 px-2 py-1 rounded-l text-xs transition-colors disabled:text-gray-400 disabled:cursor-not-allowed"
                            type="button"
                            onClick={() => moveUp(index)}
                            disabled={index === 0}
                            title="Move up"
                          >
                            ↑
                          </button>
                          <button
                            className="border border-gray-300 border-l-0 hover:bg-gray-50 text-gray-600 px-2 py-1 rounded-r text-xs transition-colors disabled:text-gray-400 disabled:cursor-not-allowed"
                            type="button"
                            onClick={() => moveDown(index)}
                            disabled={index === sentences.length - 1}
                            title="Move down"
                          >
                            ↓
                          </button>
                        </div>
                      </div>
                    </td>

                    {/* Language Text */}
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {isEditing ? (
                        <textarea
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed resize-none"
                          rows={2}
                          value={editForm.languageText}
                          onChange={(e) => setEditForm(f => ({ ...f, languageText: e.target.value }))}
                          disabled={savingId === s.id}
                        />
                      ) : (
                        <div className="max-w-xs truncate" title={s.languageText}>
                          {s.languageText}
                        </div>
                      )}
                    </td>

                    {/* English Text */}
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {isEditing ? (
                        <textarea
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed resize-none"
                          rows={2}
                          value={editForm.englishText}
                          onChange={(e) => setEditForm(f => ({ ...f, englishText: e.target.value }))}
                          disabled={savingId === s.id}
                        />
                      ) : (
                        <div className="max-w-xs truncate" title={s.englishText}>
                          {s.englishText || <span className="text-gray-400">No translation</span>}
                        </div>
                      )}
                    </td>

                    {/* Image */}
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {isEditing ? (
                        <UploadAssetButton
                          kind="image" label="Image"
                          className={"bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-1 rounded text-sm transition-colors"}
                          onFileUploaded={(file) => editForm.imageFile = file}
                        />
                      ) : s.imageUrl ? (
                        <ImageLoader storagePath={s.imageUrl} style={{ width: '60px', height: '40px', borderRadius: '6px', objectFit: 'cover' }} />
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>

                    {/* Language audio */}
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {isEditing ? (
                        <UploadAssetButton
                          kind="audio" label="Lang Audio"
                          className={"bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-1 rounded text-sm transition-colors"}
                          onFileUploaded={(file) => editForm.languageAudioFile = file}
                        />
                      ) : s.languageAudioUrl ? (
                        <AudioLoader storagePath={s.languageAudioUrl} />
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>

                    {/* English audio */}
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {isEditing ? (
                        <UploadAssetButton
                          kind="audio" label="Eng Audio"
                          className={"bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-1 rounded text-sm transition-colors"}
                          onFileUploaded={(file) => editForm.englishAudioFile = file}
                        />
                      ) : s.englishAudioUrl ? (
                        <AudioLoader storagePath={s.englishAudioUrl} />
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>

                    {/* Row actions */}
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      {isEditing ? (
                        <div className="flex gap-2">
                          <button
                            className="bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white px-3 py-1 rounded text-sm transition-colors disabled:cursor-not-allowed"
                            onClick={() => saveEdit(s.id)}
                            disabled={savingId === s.id}
                          >
                            {savingId === s.id ? 'Saving…' : 'Save'}
                          </button>
                          <button
                            className="border border-gray-300 hover:bg-gray-50 text-gray-700 px-3 py-1 rounded text-sm transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed"
                            onClick={cancelEdit}
                            disabled={savingId === s.id}
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <div className="flex gap-2">
                          <button
                            className="text-blue-600 hover:text-blue-800 px-3 py-1 border border-blue-300 hover:bg-blue-50 rounded text-sm transition-colors disabled:text-gray-400 disabled:border-gray-300 disabled:cursor-not-allowed"
                            onClick={() => startEdit(s)}
                            disabled={deletingId === s.id}
                          >
                            Edit
                          </button>
                          <button
                            className="text-red-600 hover:text-red-800 px-3 py-1 border border-red-300 hover:bg-red-50 rounded text-sm transition-colors disabled:text-gray-400 disabled:border-gray-300 disabled:cursor-not-allowed"
                            onClick={() => remove(s)}
                            disabled={deletingId === s.id}
                          >
                            {deletingId === s.id ? 'Deleting…' : 'Delete'}
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}

              {sentences.length === 0 && (
                <tr>
                  <td colSpan="7" className="px-6 py-8 text-center text-gray-500">
                    <div className="flex flex-col items-center">
                      <span className="text-4xl mb-2">📝</span>
                      <span className="text-lg font-medium">No sentences yet</span>
                      <span className="text-sm">Add your first sentence to get started</span>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

export default AdminSentences;
